import { useEffect, useRef, useCallback } from 'react'
import { trackEvent } from '../services/tracking'

/**
 * Track open/close + duration of a conditionally-rendered overlay or modal.
 * Emits view_opened on mount, view_closed on close or unmount.
 *
 * Usage:
 *   const { onClose: trackedClose } = useViewTracking('vendor_detail', onClose, {
 *     vendor_id: vendor.id,
 *   })
 */
export function useViewTracking(
  viewName: string,
  onClose: () => void,
  properties: Record<string, unknown> = {},
): { onClose: () => void } {
  const openedAt = useRef(performance.now())
  const hasClosed = useRef(false)
  const propsRef = useRef(properties)
  propsRef.current = properties

  useEffect(() => {
    trackEvent('view_opened', { view: viewName, ...propsRef.current })
    openedAt.current = performance.now()
    hasClosed.current = false

    return () => {
      if (!hasClosed.current) {
        trackEvent('view_closed', {
          view: viewName,
          duration_ms: Math.round(performance.now() - openedAt.current),
          close_method: 'unmount',
          ...propsRef.current,
        })
      }
    }
  }, [viewName])

  const trackedClose = useCallback(() => {
    if (!hasClosed.current) {
      hasClosed.current = true
      trackEvent('view_closed', {
        view: viewName,
        duration_ms: Math.round(performance.now() - openedAt.current),
        close_method: 'explicit',
        ...propsRef.current,
      })
    }
    onClose()
  }, [onClose, viewName])

  return { onClose: trackedClose }
}

/**
 * Track open/close + duration of an isOpen-controlled modal (not conditionally rendered).
 * Returns onOpen/onClose callbacks to call alongside your state setters.
 *
 * Usage:
 *   const contactTracking = useModalTracking('contact_info', { vendor_id })
 *   <button onClick={() => { setShowContact(true); contactTracking.onOpen() }}>
 *   <Modal onClose={() => { setShowContact(false); contactTracking.onClose() }}>
 */
export function useModalTracking(
  viewName: string,
  properties: Record<string, unknown> = {},
): { onOpen: () => void; onClose: () => void } {
  const openedAt = useRef(0)
  const propsRef = useRef(properties)
  propsRef.current = properties

  const onOpen = useCallback(() => {
    openedAt.current = performance.now()
    trackEvent('view_opened', { view: viewName, ...propsRef.current })
  }, [viewName])

  const onClose = useCallback(() => {
    trackEvent('view_closed', {
      view: viewName,
      duration_ms: Math.round(performance.now() - openedAt.current),
      ...propsRef.current,
    })
  }, [viewName])

  return { onOpen, onClose }
}
